const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/services/mainConfig.ts';
import _ from 'lodash';
import { BootConfigOverrides } from '../defs/bootDefs';

declare global {
    namespace NodeJS {
        interface ProcessEnv {
            NODE_ENV: 'production' | 'development' | 'test';
        }
    }
    const __PDF_BUNDLE_NAME__: string;
    const __APP__: {
        SETTINGS: any;
        CLIENT_CONFIG_PATH: string;
        MIMICS_CLIENT_CONFIG_PATH: string;
        env: any;
    };
}

export interface ContextFile {
    client: string;
    data: any;
    filename: string;
}

export type ContextFileStore = ContextFile[];

export const EnvironmentArray = ['DEV', 'TEST', 'PROD', 'PRODP', 'PRODR', 'TESTP', 'TESTR'] as const;

export type Environment = (typeof EnvironmentArray)[number];

export const ClientArray = [
    'AARREHA',
    'BASCHME',
    'BASLAND',
    'BSAG',
    'BUELACH',
    'CHUR',
    'CLARA',
    'COM',
    'FMI',
    'FRIBOURG',
    'GLARUS',
    'HGK',
    'IPW',
    'KMAG',
    'KSA',
    'KSB',
    'LACHEN',
    'LIHO',
    'LIMMI',
    'MURI',
    'OKS',
    'PUK',
    'SAMEDAN',
    'SCHAFF',
    'SCHOEN',
    'SEE',
    'SMN',
    'SOH',
    'SOHO',
    'SRO',
    'STGAG',
    'STGALLEN',
    'SUSEN',
    'SZB',
    'TRIAPLUS',
    'TRIEMLI',
    'UKBAL',
    'UKBB',
    'UPKBASEL',
    'USTER',
    'USZ',
    'WINTI',
    'ZGKS',
    'ZOFI',
] as const;

export type Client = (typeof ClientArray)[number];

/**
 * App settings shared by all products
 */
export interface GlobalCommonAppSettings {
    /**
     * URL of backend (host + path). May be an array; you have to use the balancer module in kisapi pipeline then
     */
    apiHost: string | string[];

    /**
     * URL of backend (host + path). on secured/api which is managed by openResty nginx server
     */
    apiSecuredHost?: string | string[];

    /**
     * URL of backend (host + path) for native devices. on secured/native/api which is managed by openResty nginx server
     */
    apiNativeSecuredHost?: string | string[];

    /**
     * Delay in milliseconds after which a KISAPI call is handled as timeout
     */
    apiResponseTimeout: number;

    /**
     * Language to start the app with
     */
    locale: 'de' | 'en' | 'fr';

    /**
     * KISIM-compatible id of the hospital
     */
    client: Client;

    environment: Environment;

    /**
     * KISIM-compatible id of an optional fallback hospital.
     * Used for ZOFI e.g. which has the same configuration as KSA.
     */
    mimicksClient?: Client;

    /**
     * optional description for this config to be displayed in Login-screen
     */
    configDescription?: string;

    /**
     * source of the config file:
     * undefined : config file is part of build package (legacy for KISAPP, standard for other applications)
     * 'native' : config file was set via native wrapper (meant as part of MDM)
     * 'devmode' : config file was selected by user in BootLoader
     */
    configSource?: undefined | 'native' | 'devmode';

    /**
     * Backend client_id used for authentication
     */
    clientID: string;

    /**
     * MetaKat icode of Kliniken subtree
     */
    clinicRoot: string;

    /**
     * MetaKat icode of Stationen subtree
     */
    wardRoot: string;

    /**
     * MetaKat icode of Infusionslösungen subtree. Usually this is accessed via Lookup, if this fails, the correct ICODE can be set here.
     * Otherwise leave it null or omit it.
     */
    infusionDilutionList?: string;

    /**
     * MetaKat icode of Kumulativbefund-Konfig record
     */
    labCumulConfigIcode: string;

    /**
     * MetaKat icode of Kumulativbefund-Filters subtree
     */
    labCumulDefsPcode: string;

    /**
     * List of filter definitions of cumulative reports.
     * Has been superseded by labCumulDefsPcode.
     * Is still in use to support the old Kumulativbefund.
     */
    labCumulDefs: any[];

    /**
     * Metakat icode of Berichte-Filters subtree
     */
    filterRoot: string;

    /**
     * MetaKat icode of Alle Berichte record
     */
    initFilter: string;

    /**
     * MetaKat icode of Einzelresultate-Filters subtree
     */
    larsFilterRoot: string;

    /**
     * MetaKat icode of Alle Einzelresultate record
     */
    larsInitFilter: string;

    /**
     * Base URL of our backend for push notifications
     */
    fcmBackendBaseUrl?: string;
}

export interface MainConfig {
    environment: 'web' | 'native';
    product: 'kisapp' | 'patapp' | 'teamboard' | 'statmgr' | 'patdash' | 'hospoverview' | 'common_web';
    buildDate: string;
    settings: GlobalCommonAppSettings;
}

const mainConfig: MainConfig = {
    environment: __APP__.env.APP_ENV,
    product: __APP__.env.BUILD_PART,
    buildDate: __APP__.env.BUILD_DATE,
    settings: __APP__.SETTINGS,
};

export const getMainConfig = () => {
    return mainConfig;
};

export const getMainConfigClient = (handleMimics = false) => {
    if (handleMimics && mainConfig?.settings?.mimicksClient) {
        return mainConfig.settings.mimicksClient.toLowerCase();
    }
    return mainConfig?.settings?.client?.toLowerCase?.() || '';
};

export const replaceMainConfigSettings = (newSettings: GlobalCommonAppSettings, overrides: BootConfigOverrides) => {
    mainConfig.settings = newSettings;
    if (overrides.description) {
        mainConfig.settings.configDescription = overrides.description;
    }
    if (overrides.apiUrl) {
        mainConfig.settings.apiHost = overrides.apiUrl;
    }
    if (overrides.environment) {
        mainConfig.settings.environment = overrides.environment;
    }
};

// helpers

export const storeContextFile =
    (contextFileStore: ContextFileStore, fileStorePattern: RegExp) => (ctx: any) => (file: string) => {
        const fileStoreMatch = file.match(fileStorePattern);
        if (!fileStoreMatch) {
            return;
        }
        const data = ctx(file);
        contextFileStore.push({
            client: fileStoreMatch[1] || '',
            data,
            filename: file,
        } as any);
    };

export const getRelevantContextData = (contextFileStore: ContextFileStore, skipCommon = false): ContextFile[] => {
    const client = getMainConfigClient();
    return _.compact(
        _.flatten([
            _.filter(contextFileStore, (contextFile: ContextFile) => !skipCommon && contextFile.client === 'common'),
            _.filter(contextFileStore, (contextFile: ContextFile) => contextFile.client === client),
        ])
    );
};
